import { toUserSafeError } from "./userSafeError";

import type { Client as BtxClient } from "../generated/btx/client/types.gen";
import type { Client as BpbeClient } from "../generated/bpbe/client/types.gen";
import type { Client as CentroPortalClient } from "../generated/centro-portal/client/types.gen";

type AnyClient = BtxClient | BpbeClient | CentroPortalClient;
type ClientMethod = (options: never) => Promise<unknown>;

// The wrapper only intercepts the rejection, but its erased signature can't
// reproduce the generated method's overloaded generics, so we re-assert the
// original method type via the single permitted cast.
export const guard = <F extends ClientMethod>(method: F): F =>
  ((options: never) =>
    method(options).catch((error: unknown) => {
      throw toUserSafeError(error);
    })) as F;

/**
 * Wraps a HeyApi client so a failed response validator (a thrown ZodError)
 * surfaces as a user-safe Error instead of the raw ZodError. Covers the JSON
 * verbs; streaming and the low-level `request` are left untouched.
 */
export const withUserSafeErrors = <C extends AnyClient>(client: C): C => ({
  ...client,
  get: guard(client.get),
  post: guard(client.post),
  put: guard(client.put),
  patch: guard(client.patch),
  delete: guard(client.delete),
});

/**
 * Wraps every client in a keyed collection with {@link withUserSafeErrors} so a
 * new client can't be added without the user-safe treatment. `Object.fromEntries`
 * widens the value type to the `AnyClient` union and drops the per-key mapping,
 * so the result is re-asserted as the input type `T` — sound because
 * `withUserSafeErrors` preserves each client's type.
 */
export const withUserSafeErrorsForAll = <T extends Record<string, AnyClient>>(
  clients: T,
): T =>
  Object.fromEntries(
    Object.entries(clients).map(([key, client]) => [
      key,
      withUserSafeErrors(client),
    ]),
  ) as T;
